import React, { useState, useEffect } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Edit,
  Toolbar,
  CommandColumn,
  Page,
  Inject,
  Resize,
} from "@syncfusion/ej2-react-grids";
import { registerLicense } from "@syncfusion/ej2-base";
import { useI18n } from "../../contexts/I18nContext";
import {
  getRecipientPasswords,
  createRecipientPassword,
  updateRecipientPassword,
  deleteRecipientPassword,
  deletePasswordDatabase as deletePasswordDatabaseApi,
  createOwnDatabase,
} from "../../api/passwordDatabaseApi";
import { endMyAccess } from "../../api/accessManagementApi";
import { sendRequest } from "../../services/dialogBridge";
import { OPS } from "../../services/messageOps";
import * as crypto from "../../utils/crypto";

registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1JGaF5cXGpCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdlWX5fdHRRRGNeUkJ/WEpWYEs="
);

const PasswordsPage = ({
  oid,
  passwortDatenbankId,
  initialActiveUserCount = 0,
  masterKey,
  isPaid = false,
  isOwner = true,
  onNavigate,
  onDatabaseDeleted,
  onAccessEnded,
}) => {
  const { locale, t, formatDate } = useI18n();
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(!!passwortDatenbankId);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeUserCount, setActiveUserCount] = useState(
    initialActiveUserCount
  );
  const [showPasswords, setShowPasswords] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEndAccessConfirm, setShowEndAccessConfirm] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // True while parent refreshes after delete/end-access
  const [creatingOwnDb, setCreatingOwnDb] = useState(false);
  const gridRef = React.useRef(null);
  const hasLoadedRef = React.useRef(false); // Prevent double API call

  // Track the last loaded database ID to detect changes
  const lastLoadedDbIdRef = React.useRef(null);

  // Update active user count when prop changes
  useEffect(() => {
    setActiveUserCount(initialActiveUserCount);
  }, [initialActiveUserCount]);

  // Load passwords on mount and when database changes
  useEffect(() => {
    if (oid && passwortDatenbankId && masterKey) {
      // Load if: first time OR database changed
      const dbChanged = lastLoadedDbIdRef.current !== passwortDatenbankId;
      if (!hasLoadedRef.current || dbChanged) {
        hasLoadedRef.current = true;
        lastLoadedDbIdRef.current = passwortDatenbankId;
        loadPasswords();
      }
    }
  }, [oid, passwortDatenbankId, masterKey]);

  // Create a new personal password database directly from no-DB guard
  const handleCreateOwnDatabase = async () => {
    setCreatingOwnDb(true);
    setError(null);
    try {
      // Ask sidebar to generate + encrypt a new MasterKey
      const bridgeResult = await sendRequest(OPS.ENCRYPT_NEW_MASTER_KEY);
      if (!bridgeResult?.encryptedMasterKey) {
        setError(t("Schlüssel konnte nicht erstellt werden. Bitte entsperren Sie erneut."));
        return;
      }

      // Call backend to create own DB
      const response = await createOwnDatabase({
        oid,
        encryptedMasterKey: bridgeResult.encryptedMasterKey,
        masterKeyEnvelope: bridgeResult.masterKeyEnvelope,
      });

      if (response.success) {
        setSuccess(t("Eigene Passwortdatenbank erfolgreich erstellt."));
        setTimeout(() => setSuccess(null), 3000);
        // Trigger parent refresh to pick up new DB + MasterKey
        if (onDatabaseDeleted) await onDatabaseDeleted();
      } else {
        setError(response.message || t("Fehler beim Erstellen der Datenbank"));
        setTimeout(() => setError(null), 8000);
      }
    } catch (err) {
      console.error("Error creating own database:", err);
      setError(t("Fehler beim Erstellen der Passwortdatenbank"));
      setTimeout(() => setError(null), 8000);
    } finally {
      setCreatingOwnDb(false);
    }
  };

  const loadPasswords = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getRecipientPasswords(oid, passwortDatenbankId);
      if (!result.success) {
        setError(result.message || t("Fehler beim Laden der Passwörter"));
        return;
      }
      const data = result.data || [];

      // Decrypt passwords with MasterKey
      const decryptedPasswords = await Promise.all(
        data.map(async (item) => {
          try {
            // Decrypt email and password using shared IV
            const decrypted = await crypto.decryptRecipientPassword(
              item.encryptedEmpfaengerEmail,
              item.encryptedPasswort,
              item.iv,
              masterKey
            );

            return {
              empfaengerPasswortId: item.empfaengerPasswortId,
              email: decrypted.email,
              password: decrypted.password,
              erstelltAm: new Date(item.erstelltAm),
            };
          } catch (decryptErr) {
            console.error("Error decrypting password entry:", decryptErr);
            // Return entry with error indicator
            return {
              empfaengerPasswortId: item.empfaengerPasswortId,
              email: "[Decryption Failed]",
              password: "[Decryption Failed]",
              erstelltAm: new Date(item.erstelltAm),
            };
          }
        })
      );

      setPasswords(decryptedPasswords);
    } catch (err) {
      console.error("Error loading passwords:", err);
      setError(t("Fehler beim Laden der Passwörter"));

      // Auto-hide error after 8 seconds
      setTimeout(() => {
        setError(null);
      }, 8000);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredPasswords = passwords.filter((pwd) =>
    pwd.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleActionBegin = async (args) => {
    if (args.requestType === "save") {
      // Handle inline edit save or add
      console.log("Saving:", args.data);

      // Validate: email and password must not be empty
      if (
        !args.data.email ||
        !args.data.email.trim() === "" ||
        !args.data.password ||
        args.data.password.trim() === ""
      ) {
        console.log("Validation failed: email or password is empty");
        setError(t("Email und Passwort dürfen nicht leer sein"));

        // Auto-hide error after 8 seconds
        setTimeout(() => {
          setError(null);
        }, 8000);

        args.cancel = true; // Cancel the save operation
        return;
      }

      // Clear previous errors
      setError(null);

      try {
        // Encrypt email and password with MasterKey
        const encrypted = await crypto.encryptRecipientPassword(
          args.data.email,
          args.data.password,
          masterKey
        );

        // Check if this is a new record (temp ID) or existing record
        if (args.data.empfaengerPasswortId.toString().startsWith("temp-")) {
          console.log("Adding new password:", args.data);
          console.log("Oid:", oid);
          console.log("PasswortDatenbankId:", passwortDatenbankId);

          const dto = {
            oid: oid,
            passwortDatenbankId: passwortDatenbankId,
            encryptedEmpfaengerEmail: encrypted.encryptedEmail,
            encryptedPasswort: encrypted.encryptedPassword,
            iv: encrypted.iv,
          };

          console.log("DTO being sent:", dto);
          await createRecipientPassword(dto);
          await loadPasswords(); // Reload to get real ID from backend
        } else {
          console.log("Updating existing password:", args.data);

          const dto = {
            oid: oid,
            empfaengerPasswortId: args.data.empfaengerPasswortId,
            encryptedEmpfaengerEmail: encrypted.encryptedEmail,
            encryptedPasswort: encrypted.encryptedPassword,
            iv: encrypted.iv,
          };

          await updateRecipientPassword(dto);
          await loadPasswords(); // Reload to get updated data
        }
      } catch (err) {
        console.error("Error saving password:", err);
        setError(t("Fehler beim Speichern des Passworts"));

        // Auto-hide error after 8 seconds
        setTimeout(() => {
          setError(null);
        }, 8000);

        args.cancel = true; // Cancel the save if encryption fails
      }
    } else if (args.requestType === "delete") {
      // Handle delete
      console.log("Deleting:", args.data);

      try {
        await deleteRecipientPassword(
          oid,
          args.data[0].empfaengerPasswortId
        );
        await loadPasswords(); // Reload grid
      } catch (err) {
        console.error("Error deleting password:", err);
        setError(t("Fehler beim Löschen des Passworts"));

        // Auto-hide error after 8 seconds
        setTimeout(() => {
          setError(null);
        }, 8000);
      }
    } else if (args.requestType === "cancel") {
      // Handle cancel - remove temp record if it was never saved
      console.log("Canceling edit:", args.data);

      if (
        args.data &&
        args.data.empfaengerPasswortId.toString().startsWith("temp-")
      ) {
        // This was a new unsaved record, remove it from state
        const updatedPasswords = passwords.filter(
          (p) => p.empfaengerPasswortId !== args.data.empfaengerPasswortId
        );

        // Update grid datasource directly
        if (gridRef.current) {
          gridRef.current.dataSource = updatedPasswords;
        }

        setPasswords(updatedPasswords);
      }
    }
  };

  const handleDeleteDatabase = () => {
    console.log("Delete database button clicked!");
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDatabase = async () => {
    console.log("User confirmed delete");
    setShowDeleteConfirm(false);

    try {
      console.log("Deleting password database...");

      const dto = {
        ownerOid: oid,
        passwortDatenbankId: passwortDatenbankId,
      };

      await deletePasswordDatabaseApi(dto);

      console.log("Password database deleted successfully!");

      // Show success message briefly, then refresh parent state
      setSuccess(t("Passwortdatenbank wurde erfolgreich gelöscht."));
      setPasswords([]);
      setDeleted(true);

      // After showing success, trigger parent refresh
      setTimeout(async () => {
        setSuccess(null);
        setRefreshing(true);
        if (onDatabaseDeleted) {
          await onDatabaseDeleted();
        }
        setRefreshing(false);
        setDeleted(false);
      }, 3000);
    } catch (err) {
      console.error("Error deleting password database:", err);
      setError(t("Fehler beim Löschen der Passwortdatenbank"));

      // Auto-hide error after 8 seconds
      setTimeout(() => {
        setError(null);
      }, 8000);
    }
  };

  const cancelDeleteDatabase = () => {
    console.log("User cancelled delete");
    setShowDeleteConfirm(false);
  };

  // End My Access handlers (for non-owners)
  const handleEndMyAccess = () => {
    console.log("End my access button clicked!");
    setShowEndAccessConfirm(true);
  };

  const confirmEndMyAccess = async () => {
    console.log("User confirmed end access");
    setShowEndAccessConfirm(false);

    try {
      console.log("Ending access to shared database...");

      const response = await endMyAccess(oid, passwortDatenbankId);

      if (response.success) {
        console.log("Access ended successfully!");

        // Show success message briefly, then refresh parent state
        setSuccess(t("Ihr Zugriff wurde erfolgreich beendet."));
        setPasswords([]);
        setDeleted(true);

        // After showing success, trigger parent refresh
        setTimeout(async () => {
          setSuccess(null);
          setRefreshing(true);
          if (onAccessEnded) {
            await onAccessEnded();
          }
          setRefreshing(false);
          setDeleted(false);
        }, 3000);
      } else {
        setError(response.message || t("Fehler beim Beenden des Zugriffs"));
        setTimeout(() => setError(null), 8000);
      }
    } catch (err) {
      console.error("Error ending access:", err);
      setError(t("Fehler beim Beenden des Zugriffs"));
      setTimeout(() => setError(null), 8000);
    }
  };

  const cancelEndMyAccess = () => {
    console.log("User cancelled end access");
    setShowEndAccessConfirm(false);
  };

  // Custom header template for password column with eye toggle
  const passwordHeaderTemplate = () => {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <span>{t("Passwort")}</span>
        <span
          onClick={(e) => {
            e.stopPropagation(); // Prevent column sorting/selection
            setShowPasswords((prev) => !prev);
          }}
          title={showPasswords ? t("Passwörter ausblenden") : t("Passwörter anzeigen")}
          style={{
            cursor: "pointer",
            fontSize: "16px",
            opacity: showPasswords ? 1 : 0.5,
            userSelect: "none",
            padding: "0 4px",
          }}
        >
          {showPasswords ? "\uD83D\uDC41" : "\uD83D\uDC41\u200D\uD83D\uDDE8"}
        </span>
      </div>
    );
  };

  // Custom template for password column - masks when hidden
  const passwordTemplate = (props) => {
    if (showPasswords) {
      return props.password;
    }
    return "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
  };

  // Custom template for CreatedAt column
  const dateTemplate = (props) => {
    return formatDate(props.erstelltAm);
  };

  // Guard: Transitional state after delete/end-access (success message + refresh)
  if (deleted) {
    return (
      <div style={{ padding: "20px" }}>
        <h2>{t("Passwörter verwalten")}</h2>
        {success && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
              borderRadius: "4px",
              color: "#155724",
              marginTop: "40px",
              maxWidth: "480px",
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "center",
            }}
          >
            <p style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "600" }}>
              {success}
            </p>
            <p style={{ margin: 0, fontSize: "12px", fontWeight: "400", color: "#3c763d" }}>
              {t("Seite wird in Kürze aktualisiert...")}
            </p>
          </div>
        )}
        {refreshing && (
          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <p style={{ color: "#666", fontSize: "14px" }}>
              {t("Bitte warten, Daten werden aktualisiert...")}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Guard: No active database - show choice card
  if (!passwortDatenbankId) {
    return (
      <div style={{ padding: "20px" }}>
        <h2>{t("Passwörter verwalten")}</h2>
        <div
          style={{
            maxWidth: "480px",
            margin: "60px auto",
            padding: "32px",
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          {isPaid ? (
            <>
              <h3 style={{ margin: "0 0 12px 0", color: "#333" }}>
                {t("Keine aktive Passwortdatenbank vorhanden.")}
              </h3>
              <p style={{ color: "#555", marginBottom: "24px" }}>
                {t("Was möchten Sie tun?")}
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={handleCreateOwnDatabase}
                  disabled={creatingOwnDb}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: creatingOwnDb ? "#999" : "#0078d4",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: creatingOwnDb ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "320px",
                  }}
                >
                  {creatingOwnDb ? t("Wird erstellt...") : t("Eigene Passwortdatenbank erstellen")}
                </button>
                <button
                  onClick={() => {
                    onNavigate("access", { focusSection: "join-by-code" });
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#fff",
                    color: "#0078d4",
                    border: "1px solid #0078d4",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    width: "100%",
                    maxWidth: "320px",
                  }}
                >
                  {t("Access Code eingeben")}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ margin: "0 0 12px 0", color: "#333" }}>
                {t("Sie nutzen derzeit den Free-Plan.")}
              </h3>
              <p style={{ color: "#555", marginBottom: "24px" }}>
                {t("Um den Passwort-Manager zu verwenden, können Sie eine eigene Passwortdatenbank erstellen.")}
              </p>
              <button
                onClick={handleCreateOwnDatabase}
                disabled={creatingOwnDb}
                style={{
                  padding: "10px 20px",
                  backgroundColor: creatingOwnDb ? "#999" : "#0078d4",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: creatingOwnDb ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  width: "100%",
                  maxWidth: "320px",
                }}
              >
                {creatingOwnDb ? t("Wird erstellt...") : t("Eigene Passwortdatenbank erstellen")}
              </button>
              <p
                style={{
                  color: "#888",
                  fontSize: "12px",
                  marginTop: "16px",
                  marginBottom: 0,
                }}
              >
                {t("Das Teilen von Passwortdatenbanken ist Teil des Paid-Plans.")}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Guard: MasterKey required
  if (!masterKey) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "#666" }}>
          {t("Bitte entsperren Sie zuerst die Passwortdatenbank auf der Sicherheitsseite.")}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>{t("Lade Passwörter...")}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>{t("Passwörter verwalten")}</h2>

      {error && (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            color: "#721c24",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "4px",
            color: "#155724",
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          ✅ {success}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#dc3545" }}>
              ⚠️ {t("Passwortdatenbank löschen")}
            </h3>
            <p style={{ lineHeight: "1.6", color: "#333" }}>
              {t("Möchten Sie diese Passwortdatenbank wirklich löschen?")}
            </p>
            <p style={{ lineHeight: "1.6", color: "#666" }}>
              {t("Nach diesem Vorgang verlieren alle Benutzer den Zugriff auf diese Passwortdatenbank.")}
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#dc3545",
                fontWeight: "600",
              }}
            >
              {t("Diese Aktion kann nicht rückgängig gemacht werden!")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={cancelDeleteDatabase}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("Abbrechen")}
              </button>
              <button
                onClick={confirmDeleteDatabase}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("Ja, löschen")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Access Confirmation Modal (for non-owners) */}
      {showEndAccessConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#dc3545" }}>
              ⚠️ {t("Zugriff beenden")}
            </h3>
            <p style={{ lineHeight: "1.6", color: "#333" }}>
              {t("Möchten Sie Ihren Zugriff auf diese geteilte Passwortdatenbank wirklich beenden?")}
            </p>
            <p style={{ lineHeight: "1.6", color: "#666" }}>
              {t("Sie können die Passwörter danach nicht mehr einsehen oder bearbeiten.")}
            </p>
            <p
              style={{
                lineHeight: "1.6",
                color: "#dc3545",
                fontWeight: "600",
              }}
            >
              {t("Um erneut Zugriff zu erhalten, benötigen Sie einen neuen Access Code vom Owner.")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={cancelEndMyAccess}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("Abbrechen")}
              </button>
              <button
                onClick={confirmEndMyAccess}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("Ja, Zugriff beenden")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active User Count Banner */}
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#e7f3ff",
          border: "1px solid #b3d9ff",
          borderRadius: "4px",
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <p style={{ margin: 0, fontSize: "14px" }}>
          ℹ️{" "}
          {activeUserCount === 1 && isOwner
            ? t("Nur Sie haben aktuell Zugriff auf diese Passwortdatenbank.")
            : <><strong>{activeUserCount}</strong> {t("Benutzer haben aktuell Zugriff auf diese Passwortdatenbank.")}</>}
        </p>
        {isOwner && isPaid && (
          <button
            onClick={() => onNavigate("access", { focusDbId: passwortDatenbankId })}
            style={{
              padding: "4px 12px",
              backgroundColor: "transparent",
              color: "#0078d4",
              border: "1px solid #0078d4",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            {t("Anzeigen")}
          </button>
        )}
      </div>

      {/* Search Box and Action Buttons */}
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: "1 1 160px" }}>
          <input
            type="text"
            placeholder={t("E-Mail suchen...")}
            value={searchQuery}
            onChange={handleSearch}
            style={{
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              flex: "1 1 0",
              minWidth: "0",
            }}
          />

          {/* Add Email Button */}
          <button
            onClick={() => {
              // Add new empty record to state
              const now = new Date();
              const newRecord = {
                empfaengerPasswortId: `temp-${Date.now()}`,
                email: "",
                password: "",
                erstelltAm: now,
              };

              // Add to passwords array
              setPasswords([...passwords, newRecord]);

              // Start edit on the new row after grid updates
              setTimeout(() => {
                if (gridRef.current) {
                  const rowIndex = passwords.length; // Index of new row
                  gridRef.current.selectRow(rowIndex);
                  gridRef.current.startEdit();
                }
              }, 200);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            {t("➕ E-Mail hinzufügen")}
          </button>
        </div>

        {/* Delete Database Button (Owner) or End Access Button (Non-Owner) */}
        {isOwner ? (
          <button
            onClick={handleDeleteDatabase}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              flexShrink: 0,
            }}
          >
            {t("🗑️ Passwortdatenbank löschen")}
          </button>
        ) : (
          <button
            onClick={handleEndMyAccess}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              flexShrink: 0,
            }}
          >
            {t("🚪 Zugriff beenden")}
          </button>
        )}
      </div>

      {/* Syncfusion DataGrid */}
      <GridComponent
        ref={gridRef}
        dataSource={filteredPasswords}
        locale={locale}
        allowPaging={true}
        allowResizing={true}
        pageSettings={{ pageSize: 10 }}
        editSettings={{
          allowAdding: true,
          allowEditing: true,
          allowDeleting: true,
          mode: "Normal", // Inline editing
        }}
        actionBegin={handleActionBegin}
        height="500"
        width="100%"
        gridLines="Both"
      >
        <ColumnsDirective>
          <ColumnDirective
            field="empfaengerPasswortId"
            headerText="ID"
            width="0"
            isPrimaryKey={true}
            visible={false}
          />
          <ColumnDirective field="email" headerText={t("E-Mail")} width="300" />
          <ColumnDirective
            field="password"
            headerText={t("Passwort")}
            width="200"
            template={passwordTemplate}
            headerTemplate={passwordHeaderTemplate}
          />
          <ColumnDirective
            field="erstelltAm"
            headerText={t("Erstellt am")}
            width="120"
            template={dateTemplate}
            allowEditing={false}
          />
          <ColumnDirective
            headerText={t("Aktionen")}
            width="280"
            textAlign="Center"
            commands={[
              {
                type: "Edit",
                buttonOption: { content: t("Bearbeiten"), cssClass: "e-flat" },
              },
              {
                type: "Delete",
                buttonOption: { content: t("Löschen"), cssClass: "e-flat" },
              },
              {
                type: "Save",
                buttonOption: { content: t("Speichern"), cssClass: "e-flat" },
              },
              {
                type: "Cancel",
                buttonOption: { content: t("Abbrechen"), cssClass: "e-flat" },
              },
            ]}
          />
        </ColumnsDirective>
        <Inject services={[Edit, Toolbar, CommandColumn, Page, Resize]} />
      </GridComponent>
    </div>
  );
};

export default PasswordsPage;
